/* global gtag */
import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import { API_BASE_URL, BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import ReferralLeadsDashboard from './ReferralLeadsDashboard';

const Dashboard = () => {
  const { authFetch } = useAuth();
  const [user, setUser] = useState(null);
  const [referralData, setReferralData] = useState({
    code_referrals: [],
    lead_referrals: [],
  });
  const [loadingReferrals, setLoadingReferrals] = useState(true);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
      authFetch(`${API_BASE_URL}/api/my-referrals`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setReferralData({
              code_referrals: data.code_referrals || [],
              lead_referrals: data.lead_referrals || [],
            });
          }
        })
        .catch((err) => console.error('Error fetching referrals:', err))
        .finally(() => setLoadingReferrals(false));
    }
  }, [authFetch]);

  useEffect(() => {
    // only fire once user.email_id is available
    if (!user?.email_id) return;
  
    window.gtag('event', 'view_dashboard', {
      event_category: 'Referral',
      event_label: user.email_id,
    });
  }, [user?.email_id]);
  
  const getShareMessage = () => {
    const referralCode = user?.my_referral_code || 'N/A';
    const inviteLink = `${BASE_URL}/login.html?form=signup&ref_code=${referralCode}`;
    return `🏡 Looking to Buy, Sell, or Invest in Property?
  
Check out RealtySeek.ai — your smart AI-powered real estate assistant that helps you:
  
✅ Find the best residential projects
✅ Compare property prices instantly
✅ Make confident, data-backed decisions
✅ Access exclusive insights on Gurgaon & beyond
  
It’s super easy and free to use!
  
Just sign up using this link:
👉 ${inviteLink}
  
Or enter referral code ${referralCode} when signing up.
  
🧠 Think smart. Invest smarter. Start with RealtySeek.ai today.`;
};
  

  const handleCopyReferral = () => {
    const message = getShareMessage();
    navigator.clipboard.writeText(message);
    setShowCopiedToast(true);
  
    // 🔥 Track GA event here
    gtag('event', 'copy_referral_invitation', {
      event_category: 'Referral',
      event_label: user?.my_referral_code || 'unknown',
    });
  
    setTimeout(() => setShowCopiedToast(false), 3000);
  };  

  if (!user) return <div className="dashboard-modal">Loading user...</div>;

  return (
    <div className="dashboard-modal">
      <h2 className="dashboard-title">🎉 My Referral Dashboard</h2>

      <div className="user-info-box">
        <p><strong>Name:</strong> {user.name}</p>
        <p><strong>Email:</strong> {user.email_id}</p>
        <p><strong>Referral Code:</strong> <code>{user.my_referral_code}</code></p>
      </div>

      <div className="referral-action-buttons">
        <button
          className="share-referral-btn"
          onClick={() => {
            const message = getShareMessage();
            if (navigator.share) {
              navigator.share({
                title: 'Join RealtySeek.ai – AI Real Estate Buddy',
                text: message
              });
            } else {
              handleCopyReferral();
            }

            gtag('event', 'share_referral_invitation', {
              event_category: 'Referral',
              event_label: user?.my_referral_code || 'unknown',
            });
          }}
        >
          📤 Share Invitation
        </button>

        <button className="copy-referral-btn" onClick={handleCopyReferral}>
          📋 Copy Referral Invitation
        </button>
      </div>

      <hr />

      {/* Code-Based Referrals */}
      {loadingReferrals ? (
        <p style={{ textAlign: "center", fontStyle: "italic", color: "#888" }}>
          Loading referred users...
        </p>
      ) : referralData.code_referrals.length === 0 ? (
        <div className="no-referrals-box">
          <p className="no-referrals-text">
            You haven’t referred anyone yet, but that’s totally okay! 🌱  
            <br />
            Share your referral code with friends and family to help them discover smarter property decisions with RealtySeek.ai.
            <br />
            You’ll earn <strong>₹10,000</strong> when they complete a transaction with our trusted partners! 🎉
          </p>
        </div>
      ) : (
        <>
          <h3 className="referral-subtitle">👥 Code Referral Leads</h3>

          <div className="referrals-exist-message">
            <p className="referrals-exist-text">
              You have referred {referralData.code_referrals.length} user{referralData.code_referrals.length > 1 ? 's' : ''}. Keep it up!
            </p>
          </div>

          <div className="responsive-referral-table">
            <table className="referral-leads-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {referralData.code_referrals.map((ref) => (
                  <tr key={ref.referred_user_id}>
                    <td data-label="User">{ref.name}</td>
                    <td data-label="Joined">{new Date(ref.joined_on).toLocaleDateString()}</td>
                    <td data-label="Status">{ref.transaction_status}</td>
                    <td data-label="Remarks">{ref.partner_updates || ref.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Property Referral Leads */}
      <ReferralLeadsDashboard
        codeReferrals={referralData.code_referrals}
        leadReferrals={referralData.lead_referrals}
      />

      {showCopiedToast && (
        <div className="copied-toast">✅ Referral message copied!</div>
      )}
    </div>
  );
};

export default Dashboard;
